/*----------------------------------
- HOOK: DOMAINS FILM
----------------------------------*/

/**
 * The product film of `/domains`' closing block, played only when the visitor asks: until Play is pressed the page
 * holds a poster and no video element, so no video byte loads (the mock's "Play animation" pill, docs/mockups/
 * website-domains.html). The cut (landscape or the 9:16 portrait) is chosen at the press from the film catalog's own
 * breakpoint, which the page sheet's portrait box follows.
 *
 * Not `useScreenerFilmVideo` (the `/product/search` film): that one loads and plays by itself once the figure is in
 * view, which this page's mock replaced with an explicit press. The film plays once and stops on its last frame.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { useCallback, useRef, useState } from 'preact/hooks';

import {
    HOME_SCREENER_BLUEPRINT,
    type HomeScreenerBlueprintVariant,
} from '@/common/catalogs/landing/homeScreenerBlueprint';
import { appErrorReporter } from '@/common/libs/errorReporting';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainsFilm = {
    /** The cut chosen at the first press; null until then, so the video element is not rendered. */
    variant: HomeScreenerBlueprintVariant | null;
    isPlaying: boolean;
    /** The video has painted a frame: the poster can give way to it. */
    showVideo: boolean;
    failed: boolean;
    videoRef: { current: HTMLVideoElement | null };
    toggle: () => void;
    onPlaying: () => void;
    onPause: () => void;
    onError: () => void;
};

/*----------------------------------
- HELPERS
----------------------------------*/

const readVariant = (): HomeScreenerBlueprintVariant =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(HOME_SCREENER_BLUEPRINT.mobileMediaQuery).matches
        ? 'mobile'
        : 'desktop';

/*----------------------------------
- HOOK
----------------------------------*/

const useDomainsFilm = (): DomainsFilm => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [variant, setVariant] = useState<HomeScreenerBlueprintVariant | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showVideo, setShowVideo] = useState(false);
    const [failed, setFailed] = useState(false);

    const play = useCallback((video: HTMLVideoElement) => {
        // A film that ended rewinds: Play after the last frame plays it again from the start.
        if (video.ended) video.currentTime = 0;
        const request = video.play();
        if (request)
            void request.catch((error: Error) => {
                appErrorReporter.app.handleError(error);
                setIsPlaying(false);
            });
    }, []);

    const toggle = useCallback(() => {
        const video = videoRef.current;
        // First press: render the element; it starts itself (`autoPlay`) once its sources are in the DOM.
        if (!video) {
            setVariant(readVariant());
            return;
        }
        if (isPlaying) video.pause();
        else play(video);
    }, [isPlaying, play]);

    const onPlaying = useCallback(() => {
        setIsPlaying(true);
        setShowVideo(true);
    }, []);

    const onPause = useCallback(() => setIsPlaying(false), []);

    const onError = useCallback(() => {
        appErrorReporter.app.handleError(videoRef.current?.error ?? new Error('The /domains product film could not be loaded.'));
        setIsPlaying(false);
        setShowVideo(false);
        setFailed(true);
    }, []);

    return { variant, isPlaying, showVideo, failed, videoRef, toggle, onPlaying, onPause, onError };
};

export default useDomainsFilm;
